import { useEffect, useState, type FormEvent } from "react";
import { CUSTOMER_PRIVACY_POLICY, isPublicPrivacyNoticeVersion } from "@tinkr/shared/customer-privacy";
import { CountryClosed, isProviderCountryClosed } from "./CountryClosed";
import { translate } from "../i18n";
import { BRAND } from "../lib/brand";
import type { PublicPrivacyNoticeVersion } from "@tinkr/shared/privacy-notice";
import type { AppLocale } from "../i18n";
import { CheckoutApiError, getHandoffSummary, isDoodlesPricePending, parseDoodlesPricing, privateCheckoutPath,
  startHandoffVerification, verifyHandoff, type DoodlesPricing, type HandoffSummary, type ShippingAddress } from "../lib/api";
import { BACKGROUND_POLL_DELAY_MS, PENDING_STILL_WORKING_AFTER_MS, retryWhilePending } from "../lib/pending-retry";
import { formatMoney } from "../lib/money";
import { flowCopy, genericPrintLabel } from "./flow-copy";
import { useVerificationCooldown, verificationError } from "./verification-feedback";

type AddressValues = { email: string; postalCode: string; prefecture: string; city: string; town: string;
  building: string; recipientName: string; recipientNameKana: string; phone: string;
  line1: string; line2: string; state: string };
const empty: AddressValues = { email: "", postalCode: "", prefecture: "", city: "", town: "", building: "",
  recipientName: "", recipientNameKana: "", phone: "", line1: "", line2: "", state: "" };
type SupportedHandoffSummary = Omit<HandoffSummary, "privacyNoticeVersion"> & {
  privacyNoticeVersion: PublicPrivacyNoticeVersion;
};
/** Server work the customer only waits for: the multicolor price or the print artifact. */
function isHandoffPending(error: unknown): boolean {
  return isDoodlesPricePending(error) || (error instanceof CheckoutApiError && error.code === "doodles_artifact_not_ready");
}
type PricePending = { pricing: DoodlesPricing | null; stillWorking: boolean };

function supportedNoticeVersion(version: string): version is PublicPrivacyNoticeVersion {
  return isPublicPrivacyNoticeVersion(version) &&
    CUSTOMER_PRIVACY_POLICY.compatibleCustomerNoticeVersions.includes(version);
}

/** Link the exact notice the handoff records; an older API cutover keeps its versioned page. */
function privacyNoticeHref(version: PublicPrivacyNoticeVersion): string {
  return version === CUSTOMER_PRIVACY_POLICY.noticeVersion ? "/legal/privacy" : `/legal/privacy/${version}`;
}

export function Handoff({ token, locale, onVerified }: { token: string; locale: AppLocale; onVerified: (path: string) => void }) {
  const copy = flowCopy(locale);
  const [summary, setSummary] = useState<SupportedHandoffSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [closed, setClosed] = useState(false);
  const [providerClosed, setProviderClosed] = useState(false);
  const [pricePending, setPricePending] = useState<PricePending | null>(null);
  const [fields, setFields] = useState<AddressValues>(empty);
  const [attempted, setAttempted] = useState(false);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cooldown = useVerificationCooldown(locale);

  useEffect(() => {
    let active = true;
    let waitTimer: ReturnType<typeof setTimeout> | undefined;
    let stillWorkingTimer: ReturnType<typeof setTimeout> | undefined;
    // The pending wait's resolver: unmount settles it so the loop sees
    // isCancelled() and ends, instead of awaiting a cleared timer forever.
    let wakeWait: (() => void) | undefined;
    const wait = (delayMs: number) => new Promise<void>(resolve => {
      wakeWait = resolve;
      waitTimer = setTimeout(() => { wakeWait = undefined; resolve(); }, delayMs);
    });
    setLoading(true); setLoadError(false); setClosed(false); setSummary(null); setPricePending(null);
    const load = async () => {
      const result = await getHandoffSummary(token);
      if (!supportedNoticeVersion(result.privacyNoticeVersion)) throw new Error("Unsupported privacy notice");
      return { ...result, privacyNoticeVersion: result.privacyNoticeVersion };
    };
    const options = {
      isPending: isHandoffPending,
      isCancelled: () => !active,
      wait,
      onPending: (error: unknown) => {
        const pricing = error instanceof CheckoutApiError ? parseDoodlesPricing(error.details?.pricing) : null;
        setPricePending(prev => ({ pricing: pricing ?? prev?.pricing ?? null, stillWorking: prev?.stillWorking ?? false }));
        stillWorkingTimer ??= setTimeout(() => {
          if (active) setPricePending(prev => prev ? { ...prev, stillWorking: true } : prev);
        }, PENDING_STILL_WORKING_AFTER_MS);
      },
    };
    // The bounded run first, then background reads with no give-up state: the
    // price or artifact lands server-side without any customer action.
    void (async () => {
      let result = await retryWhilePending(load, options);
      while (result.kind === "exhausted") result = await retryWhilePending(load, { ...options, delays: [BACKGROUND_POLL_DELAY_MS] });
      if (result.kind === "ready" && active) { setSummary(result.value); setPricePending(null); }
    })()
      .catch(cause => { if (active) { setPricePending(null); setLoadError(true); setClosed(cause instanceof CheckoutApiError && cause.code === "country_closed"); setProviderClosed(isProviderCountryClosed(cause)); } })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      if (waitTimer !== undefined) clearTimeout(waitTimer);
      wakeWait?.();
      wakeWait = undefined;
      if (stillWorkingTimer !== undefined) clearTimeout(stillWorkingTimer);
    };
  }, [token]);

  const update = (key: keyof AddressValues, value: string) => setFields(old => ({ ...old, [key]: value }));
  const jp = summary?.delivery.destinationCountryCode === "JP";
  const countryName = summary ? new Intl.DisplayNames([locale], { type: "region" })
    .of(summary.delivery.destinationCountryCode) ?? summary.delivery.destinationCountryCode : "";
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim());
  const postalValid = jp ? /^\d{3}-?\d{4}$/.test(fields.postalCode.trim())
    : /^[A-Za-z0-9 -]{3,40}$/.test(fields.postalCode.trim());
  const phoneValid = jp ? /^[\d+()\- ]{8,16}$/.test(fields.phone.trim())
    : !fields.phone.trim() || /^[\d+()\- ]{8,40}$/.test(fields.phone.trim());
  const valid = emailValid && postalValid && phoneValid && !!fields.recipientName.trim() && !!fields.city.trim() &&
    (jp ? !!fields.prefecture.trim() && !!fields.town.trim() : !!fields.line1.trim());

  function shipping(): ShippingAddress {
    const countryCode = summary!.delivery.destinationCountryCode;
    if (countryCode === "JP") return { countryCode: "JP", postalCode: fields.postalCode.trim(),
      prefecture: fields.prefecture.trim(), city: fields.city.trim(), town: fields.town.trim(),
      building: fields.building.trim(), recipientName: fields.recipientName.trim(),
      recipientNameKana: fields.recipientNameKana.trim(), phone: fields.phone.trim() };
    return { countryCode, recipientName: fields.recipientName.trim(), line1: fields.line1.trim(),
      line2: fields.line2.trim(), city: fields.city.trim(), state: fields.state.trim(),
      postalCode: fields.postalCode.trim(), phone: fields.phone.trim() };
  }

  async function send(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault(); setAttempted(true); setError(null);
    if (!summary || !valid || busy || cooldown.waiting) return;
    setBusy(true);
    try {
      const result = await startHandoffVerification(token, { email: fields.email.trim(), locale,
        shipping: shipping(), privacyNoticeVersion: summary.privacyNoticeVersion });
      setVerificationId(result.verificationId);
      setCode(""); cooldown.start();
    } catch (cause) {
      if (cause instanceof CheckoutApiError && cause.code === "country_closed") { setClosed(true); setProviderClosed(isProviderCountryClosed(cause)); }
      setError(verificationError(cause, locale, copy.sendFailed));
      if (cause instanceof CheckoutApiError && cause.code === "verification_rate_limited") cooldown.start();
    } finally { setBusy(false); }
  }

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError(null);
    if (!verificationId || !/^\d{8}$/.test(code) || busy) return;
    setBusy(true);
    try {
      const result = await verifyHandoff(token, verificationId, code);
      const path = privateCheckoutPath(result.checkoutUrl);
      if (!path) throw new Error("Invalid private checkout URL");
      setCode(""); setVerificationId(null); setFields(empty);
      onVerified(path);
    } catch (cause) {
      if (cause instanceof CheckoutApiError && cause.code === "country_closed") { setClosed(true); setProviderClosed(isProviderCountryClosed(cause)); }
      setError(verificationError(cause, locale, copy.codeFailed));
    } finally { setBusy(false); }
  }

  if (loading && pricePending) {
    const { pricing } = pricePending;
    return <main className="co-center co-price-pending">
      <div className="co-spinner" aria-hidden />
      <h1>{translate(locale, "review.pricing.calculating")}</h1>
      <p>{translate(locale, "review.pricing.calculatingBody")}</p>
      {pricing && <section className="co-card" style={{ width: "100%" }}>
        <div className="co-price-row"><span>{translate(locale, "review.pricing.from")}</span>
          <span>{formatMoney(pricing.sizeBaseMinor + pricing.multicolorFloorMinor, pricing.currency, locale)}</span></div>
      </section>}
      <p role="status" aria-live="polite">{pricePending.stillWorking ? translate(locale, "review.pricing.stillWorking") : ""}</p>
    </main>;
  }
  if (loading) return <main className="co-state"><div className="co-spinner" aria-hidden /><p>{copy.contactTitle}</p></main>;
  if (closed) return <CountryClosed locale={locale} providerClosed={providerClosed} />;
  // Partner handoff tokens are not order-access tokens and carry no first-party
  // storefront return, so a failed introduction offers the brand site and support.
  if (loadError || !summary) return <main className="co-state"><h1>{copy.contactTitle}</h1><p>{copy.unavailable}</p>
    <div className="co-stack">
      <a className="co-cta" href={BRAND.marketingUrl}>{translate(locale, "error.backToSite")}</a>
      <a className="co-btn-secondary" href={`mailto:${BRAND.supportEmail}`}>{translate(locale, "cancelled.help")}</a>
    </div></main>;

  return <main className="co-page co-handoff">
    <div className="co-handoff-main">
      <section className="co-card">
        <h1 className="co-card-title">{verificationId ? copy.codeTitle : copy.contactTitle}</h1>
        <p>{verificationId ? copy.codeIntro : copy.contactIntro}</p>
        <div className="co-price-row"><span>{copy.item}</span><strong>{summary.item.title === "3D print" ? genericPrintLabel(locale) : summary.item.title}</strong></div>
        <div className="co-price-row"><span>{copy.destination}</span><strong>{countryName}</strong></div>
        <div className="co-price-row co-price-total"><span>{copy.total}</span><strong>{formatMoney(summary.price.totalMinor, summary.price.currency, locale)}</strong></div>
      </section>
      {verificationId ? <form className="co-card co-handoff-form" onSubmit={event => void verify(event)}>
        <label>{copy.code}<input autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{8}" maxLength={8}
          value={code} onChange={event => setCode(event.target.value.replace(/\D/g, "").slice(0, 8))} /></label>
        {error && <p role="alert" className="co-handoff-error">{error}</p>}
        <button className="co-cta" type="submit" disabled={busy || !/^\d{8}$/.test(code)}>{busy ? copy.verifying : copy.verify}</button>
        <button className="co-btn-secondary" type="button" disabled={busy || cooldown.waiting}
          onClick={() => void send()}>{cooldown.label}</button>
        <button className="co-btn-secondary" type="button" disabled={busy} onClick={() => {
          setVerificationId(null); setCode(""); setError(null);
        }}>{copy.retry}</button>
      </form> : <form className="co-card co-handoff-form" onSubmit={event => void send(event)} noValidate>
        <label>{copy.email}<input type="email" autoComplete="email" value={fields.email}
          aria-invalid={attempted && !emailValid} onChange={event => update("email", event.target.value)} required /></label>
        {jp ? <>
          <label>{copy.postal}<input autoComplete="postal-code" inputMode="numeric" value={fields.postalCode}
            aria-invalid={attempted && !postalValid} onChange={event => update("postalCode", event.target.value)} required /></label>
          <label>{copy.prefecture}<input autoComplete="address-level1" value={fields.prefecture}
            aria-invalid={attempted && !fields.prefecture.trim()} onChange={event => update("prefecture", event.target.value)} required /></label>
          <label>{copy.city}<input autoComplete="address-level2" value={fields.city}
            aria-invalid={attempted && !fields.city.trim()} onChange={event => update("city", event.target.value)} required /></label>
          <label>{copy.town}<input value={fields.town} aria-invalid={attempted && !fields.town.trim()}
            onChange={event => update("town", event.target.value)} required /></label>
          <label>{copy.building}<input value={fields.building} onChange={event => update("building", event.target.value)} /></label>
          <label>{copy.recipient}<input autoComplete="name" value={fields.recipientName}
            aria-invalid={attempted && !fields.recipientName.trim()} onChange={event => update("recipientName", event.target.value)} required /></label>
          <label>{copy.kana}<input value={fields.recipientNameKana} onChange={event => update("recipientNameKana", event.target.value)} /></label>
        </> : <>
          <label>{copy.recipient}<input autoComplete="name" value={fields.recipientName}
            aria-invalid={attempted && !fields.recipientName.trim()} onChange={event => update("recipientName", event.target.value)} required /></label>
          <label>{copy.line1}<input autoComplete="address-line1" value={fields.line1}
            aria-invalid={attempted && !fields.line1.trim()} onChange={event => update("line1", event.target.value)} required /></label>
          <label>{copy.line2}<input autoComplete="address-line2" value={fields.line2} onChange={event => update("line2", event.target.value)} /></label>
          <label>{copy.city}<input autoComplete="address-level2" value={fields.city}
            aria-invalid={attempted && !fields.city.trim()} onChange={event => update("city", event.target.value)} required /></label>
          <label>{copy.state}<input autoComplete="address-level1" value={fields.state} onChange={event => update("state", event.target.value)} /></label>
          <label>{copy.postal}<input autoComplete="postal-code" value={fields.postalCode}
            aria-invalid={attempted && !postalValid} onChange={event => update("postalCode", event.target.value)} required /></label>
        </>}
        <label>{copy.phone}<input type="tel" autoComplete="tel" value={fields.phone}
          aria-invalid={attempted && !phoneValid} onChange={event => update("phone", event.target.value)} required={jp} /></label>
        {attempted && !valid && <p role="alert" className="co-handoff-error">{copy.invalidForm}</p>}
        {error && <p role="alert" className="co-handoff-error">{error}</p>}
        <p className="co-handoff-notice">{copy.notice} <a href={privacyNoticeHref(summary.privacyNoticeVersion)}
          target="_blank" rel="noopener noreferrer">{copy.privacyLink}</a></p>
        <button className="co-cta" type="submit" disabled={busy || cooldown.waiting}>{busy ? copy.sending : cooldown.waiting ? cooldown.label : copy.sendCode}</button>
      </form>}
    </div>
  </main>;
}
